import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import bannerDesktop from './images/atto2/1stBanner.jpg';
import bannerPortrait from './images/atto2/1stBanner-portrait.jpg';
import poster from './images/atto2/atto2-coming-soon.jpg';
import exterior01 from './images/atto2/exterior-01.jpg';
import exterior02 from './images/atto2/exterior-02.jpg';
import exterior03 from './images/atto2/exterior-03.jpg';
import carGreen from './images/atto2/car-green.png';
import carGrey from './images/atto2/car-grey.png';
import carWhite from './images/atto2/car-white.png';
import carBlack from './images/atto2/car-black.png';
import interior01 from './images/atto2/interior-01.jpg';
import interior02 from './images/atto2/interior-02.jpg';
import interior03 from './images/atto2/interior-03.jpg';
import interior04 from './images/atto2/interior-04.jpg';
import interior05 from './images/atto2/interior-05.jpg';
import interior06 from './images/atto2/interior-06.jpg';
import charging01 from './images/atto2/charging-01.jpg';
import safety01 from './images/atto2/safety-01.jpg';
import tech01 from './images/atto2/tech-01.jpg';
import tech02 from './images/atto2/tech-02.jpg';
import tech04 from './images/atto2/tech-04.jpg';
import tech05 from './images/atto2/tech-05.jpg';
import connectivity01 from './images/atto2/connectivity-01.jpg';
import connectivity02 from './images/atto2/connectivity-02.jpg';
import v2l01 from './images/atto2/v2l-01.jpg';
import PageSeo, { breadcrumbJsonLd, keywordSets } from './PageSeo';
import CarColorShowcase from './CarColorShowcase';
import { createWhatsAppUrl } from './siteConfig';

// Status per pertengahan Oktober 2026. Harga, spesifikasi, dan tanggal
// peluncuran resmi untuk Indonesia belum diumumkan BYD Motor Indonesia.
// Foto dan deskripsi fitur mengikuti halaman resmi BYD Eropa (byd.com/eu);
// kelengkapan versi Indonesia bisa berbeda.
const UPDATED_AT = '15 Oktober 2026';

const WA_UPDATE = createWhatsAppUrl(
  'Halo Junius, saya ingin didaftarkan untuk update BYD Atto 2 (harga, spesifikasi resmi, jadwal peluncuran, dan penawaran eksklusif) di BYD BSD Serpong.',
);
const WA_BOOKING = createWhatsAppUrl(
  'Halo Junius, saya tertarik melakukan pemesanan awal BYD Atto 2. Mohon info ketentuan tanda jadi, pilihan tipe, dan estimasi pengiriman.',
);

const timeline = [
  {
    date: '13 Oktober 2026',
    title: 'Dipamerkan perdana di Indonesia',
    desc: 'BYD Indonesia menampilkan Atto 2 saat peluncuran Danza Z9 GT dan New D9 di Jakarta Selatan. Unit dipajang, spesifikasi lokal belum dirilis.',
  },
  {
    date: '14 Oktober 2026',
    title: 'Blade Battery 2.0 dan Flash Charging dipastikan',
    desc: 'Presiden Direktur PT BYD Motor Indonesia menegaskan Atto 2 versi Indonesia memakai Blade Battery generasi kedua yang mendukung Flash Charging, menunggu kesiapan pabrik.',
  },
  {
    date: 'Oktober 2026',
    title: 'Pemesanan awal mulai dibuka',
    desc: 'Jaringan dealer BYD sudah menerima pemesanan awal. Ketentuan tanda jadi dan estimasi pengiriman dikonfirmasi langsung oleh Junius.',
  },
  {
    date: 'Menunggu pengumuman',
    title: 'Peluncuran resmi, harga, dan spesifikasi Indonesia',
    desc: 'Belum ada tanggal resmi. Peluncuran diperkirakan pada ajang otomotif akhir 2026, mengikuti kesiapan produksi BYD di Indonesia.',
  },
];

const exteriorGallery = [
  {
    src: exterior01,
    ratio: 56,
    ratioMd: 83.3,
    title: 'Dragon Face generasi baru',
    desc: 'Bahasa desain keluarga BYD oleh Wolfgang Egger: overhang pendek dan lekukan bodi bertenaga memberi kesan sporty sekaligus kokoh.',
  },
  {
    src: exterior02,
    ratio: 133.3,
    title: 'LED tajam, lampu belakang Möbius',
    desc: 'Lampu depan LED dengan DRL khas dan lampu belakang menerus bermotif cincin Möbius.',
  },
  {
    src: exterior03,
    ratio: 133.3,
    title: 'Lincah di kota',
    desc: 'Panjang 4,3 meter dengan radius putar kecil, mudah bermanuver di jalan padat dan parkir sempit.',
  },
];

// Nama warna mengikuti BYD Eropa; render mobil dipotong dari foto resmi
// (latar dihapus). Pilihan warna untuk Indonesia menunggu pengumuman resmi.
const exteriorColors = [
  { label: 'Hiking Green', start: '#d3d9c3', end: '#98a487', image: carGreen },
  { label: 'Climbing Grey', start: '#7a7f88', end: '#2a2d32', image: carGrey },
  { label: 'Skiing White', start: '#ffffff', end: '#cdd1d6', image: carWhite },
  { label: 'Cosmos Black', start: '#9AA1AA', end: '#030303', image: carBlack },
];

// Angka versi Eropa (byd.com/eu); versi Indonesia menyusul.
const showcaseStats = [
  { label: 'Jarak tempuh WLTP (versi Eropa)', value: 312, unit: 'km' },
  { label: '0-100 km/jam (versi Eropa)', value: 7.9, unit: 'detik', decimals: 1 },
  { label: 'Panjang bodi', value: 4.31, unit: 'm', decimals: 2 },
];

const interiorGallery = [
  {
    src: interior01,
    ratio: 56,
    ratioMd: 83.3,
    title: 'Material berkualitas',
    desc: 'Jok vegan leather ramah lingkungan, dashboard dan panel pintu dengan material premium.',
  },
  {
    src: interior02,
    ratio: 133.3,
    title: 'Layar putar pintar',
    desc: 'Head unit yang bisa diputar, memadukan navigasi, aplikasi, dan hiburan.',
  },
  {
    src: interior03,
    ratio: 133.3,
    title: 'Kursi ergonomis',
    desc: 'Pengaturan elektrik dan penghangat kursi, posisi duduk tinggi untuk visibilitas di lalu lintas padat.',
  },
  {
    src: interior04,
    ratio: 79,
    title: 'Ruang lega untuk semua',
    desc: 'Baterai ringkas e-Platform 3.0 membebaskan ruang kepala dan kaki di depan maupun belakang.',
  },
  {
    src: interior05,
    ratio: 79,
    title: 'Sunroof panoramik',
    desc: 'Kabin terasa lapang, ditambah ambient light dan wireless charging.',
  },
  {
    src: interior06,
    ratio: 79,
    title: 'Bagasi 400 sampai 1.340 liter',
    desc: 'Lipat kursi belakang untuk 1.340 liter, plus penyimpanan pintar di kabin.',
  },
];

const techFeatures = [
  {
    src: tech02,
    title: 'Blade Battery',
    desc: 'Baterai LFP BYD yang lolos rangkaian uji ekstrem. Versi Indonesia dipastikan memakai generasi kedua, Blade Battery 2.0.',
  },
  {
    src: charging01,
    title: 'Pengisian cepat',
    desc: 'Versi Eropa: DC 65 kW, 30-80% dalam 28 menit. Versi Indonesia memakai Flash Charging dengan klaim 10-97% sekitar 9 menit.',
  },
  {
    src: tech01,
    title: 'e-Platform 3.0',
    desc: 'Platform khusus mobil listrik murni untuk efisiensi, keselamatan, dan jarak tempuh yang lebih baik di suhu rendah.',
  },
  {
    src: tech04,
    title: 'CTB (Cell-to-Body)',
    desc: 'Baterai menyatu dengan bodi membentuk struktur sandwich, kekakuan torsional naik 32% untuk keselamatan dan handling.',
  },
  {
    src: tech05,
    title: 'Powertrain 8-in-1',
    desc: 'VCU, BMS, MCU, PDU, DC-DC, onboard charger, motor, dan transmisi dalam satu modul. Heat pump hemat energi sebagai standar.',
  },
  {
    src: safety01,
    title: 'ADAS standar',
    desc: 'Sistem bantuan berkendara lengkap tersedia standar di semua tipe, membuat perjalanan lebih aman dan intuitif.',
  },
  {
    src: connectivity01,
    title: 'BYD Intelligent Cockpit',
    desc: 'Koneksi 4G, navigasi standar, Android Auto dan Apple CarPlay, kontrol suara, serta pembaruan OTA.',
  },
  {
    src: connectivity02,
    title: 'Kunci NFC dan remote AC',
    desc: 'Masuk tanpa kunci dengan kartu atau ponsel, nyalakan AC dari jauh sebelum berangkat.',
  },
  {
    src: v2l01,
    title: 'V2L: Charge your life',
    desc: 'Jadikan Atto 2 power bank berjalan untuk piknik, camping, atau kebutuhan darurat.',
  },
];

const confirmed = [
  'SUV kompak listrik di bawah Atto 3, pelengkap lini Atto 1 dan Atto 3.',
  'Blade Battery 2.0 (LFP) dengan dukungan Flash Charging. Klaim BYD: 10-70% sekitar 5 menit, 10-97% sekitar 9 menit dengan BYD Flash Charger.',
  'Dua kode tipe sudah terdaftar di data NJKB Samsat DKI Jakarta (SC3E-STD-1 dan SC3E-ETD-1), indikasi dua varian: Standard dan Extended Range.',
  'Dibangun di atas e-Platform 3.0, penggerak roda depan.',
];

const pending = [
  'Harga OTR Jakarta. Nilai NJKB (Rp245 juta sampai Rp281 juta) bukan harga jual dan belum termasuk pajak, BBN, serta komponen lain.',
  'Kapasitas baterai, tenaga motor, dan jarak tempuh versi Indonesia.',
  'Pilihan warna, daftar fitur, dan kelengkapan per varian untuk pasar Indonesia.',
  'Tanggal peluncuran dan jadwal pengiriman unit.',
];

// Referensi dari Atto 2 versi Eropa (byd.com/eu) dan pasar lain.
const referenceSpecs = [
  ['Segmen', 'Compact SUV listrik, 5 penumpang'],
  ['Dimensi (P x L x T)', '4.310 x 1.830 x 1.675 mm'],
  ['Wheelbase', '2.620 mm'],
  ['Ground clearance', '165 mm'],
  ['Platform', 'e-Platform 3.0, penggerak roda depan, CTB'],
  ['Jarak tempuh (versi Eropa)', 'Hingga 312 km WLTP kombinasi'],
  ['Akselerasi 0-100 km/jam (versi Eropa)', '7,9 detik, kecepatan maksimum 160 km/jam'],
  ['Pengisian DC (versi Eropa)', '65 kW, 30-80% dalam 28 menit'],
  ['Baterai versi Indonesia', 'Blade Battery 2.0 dengan Flash Charging (kapasitas menyusul)'],
  ['Layar', 'Head unit putar 12,8 inci, panel instrumen digital 8,8 inci'],
  ['Kenyamanan', 'Sunroof panoramik, wireless charging, ambient light, kursi elektrik berpenghangat, heat pump'],
  ['Bagasi', '400 liter, hingga 1.340 liter dengan kursi belakang dilipat'],
  ['Konektivitas', '4G, Android Auto, Apple CarPlay, kontrol suara, OTA, kunci NFC'],
];

const faqs = [
  {
    q: 'Kapan BYD Atto 2 resmi meluncur di Indonesia?',
    a: 'BYD Motor Indonesia belum mengumumkan tanggal resmi. Unitnya sudah dipamerkan di Jakarta pada 13 Oktober 2026 dan peluncuran menunggu kesiapan pabrik. Daftarkan nomor Anda ke Junius agar langsung dikabari saat tanggalnya keluar.',
  },
  {
    q: 'Berapa harga BYD Atto 2?',
    a: 'Harga resmi belum dirilis. Data NJKB Samsat DKI mencatat Rp245 juta sampai Rp281 juta untuk dua tipe, tetapi NJKB bukan harga OTR. Perkiraan harga jual akan disampaikan begitu BYD mengumumkannya.',
  },
  {
    q: 'Apakah sudah bisa pesan BYD Atto 2 di BYD BSD Serpong?',
    a: 'Pemesanan awal sudah bisa dilakukan melalui Junius. Ketentuan tanda jadi, pilihan tipe, dan estimasi pengiriman dikonfirmasi langsung saat Anda menghubungi.',
  },
  {
    q: 'Apa bedanya Atto 2 dengan Atto 1 dan Atto 3?',
    a: 'Atto 1 adalah city car listrik untuk harian, Atto 3 SUV listrik berukuran lebih besar. Atto 2 mengisi posisi di antara keduanya sebagai SUV kompak dengan Blade Battery 2.0 dan Flash Charging.',
  },
];

function JelajahiAtto2() {
  const isMobile = useMediaQuery('(max-width:768px)');
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  return (
    <>
      <PageSeo
        title='BYD Atto 2 Indonesia Segera Hadir | Daftar Update di BYD BSD Serpong'
        description='BYD Atto 2 segera hadir di Indonesia dengan Blade Battery 2.0 dan Flash Charging. Lihat foto eksterior, interior, pilihan warna, status peluncuran, referensi spesifikasi, dan daftar untuk update eksklusif bersama Junius di BYD BSD Serpong.'
        keywords={[
          ...keywordSets.localDealer,
          'BYD Atto 2',
          'BYD Atto 2 Indonesia',
          'harga BYD Atto 2',
          'BYD Atto 2 BSD Serpong',
          'SUV kompak listrik BYD',
          'BYD Atto 2 pre-order',
        ]}
        path='/jelajahi-atto2'
        image={bannerDesktop}
        jsonLd={[
          breadcrumbJsonLd([
            { name: 'Beranda', path: '/' },
            { name: 'BYD Atto 2', path: '/jelajahi-atto2' },
          ]),
          faqJsonLd,
        ]}
      />

      <Box component='main' sx={{ width: '100%', overflow: 'hidden' }}>
        <HeroSection isMobile={isMobile} />
        <TimelineSection />
        <ExteriorSection />
        <CarColorShowcase
          title='BYD Atto 2'
          kicker='Exterior'
          description='Empat warna yang ditawarkan di Eropa: Hiking Green, Climbing Grey, Skiing White, dan Cosmos Black. Pilihan warna untuk Indonesia menunggu pengumuman resmi BYD.'
          colors={exteriorColors}
          defaultColor='Hiking Green'
          imageRatio={50.4}
          stats={showcaseStats}
          cta={{ label: 'Daftar Update', href: WA_UPDATE, external: true }}
        />
        <InteriorSection />
        <TechSection />
        <StatusSection />
        <ReferenceSpecSection />
        <LineupSection />
        <FaqSection />
        <CtaSection />
      </Box>
    </>
  );
}

function HeroSection({ isMobile }) {
  return (
    <>
    <Box
      sx={{
        position: 'relative',
        minHeight: { xs: '60vh', md: '100vh' },
        display: 'flex',
        alignItems: 'flex-end',
        overflow: 'hidden',
        backgroundColor: '#0d1b2a',
        color: '#fff',
      }}
    >
      <Box
        component='img'
        src={isMobile ? bannerPortrait : bannerDesktop}
        alt='BYD Atto 2 tampak depan di tepi laut, warna Hiking Green'
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          // Mobil berada di tengah banner; geser ke kanan di desktop supaya
          // teks di kiri tidak menutupi bodi.
          objectPosition: { xs: 'center 35%', md: '6% center' },
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          background: {
            xs: 'linear-gradient(180deg, rgba(5,16,30,0.55) 0%, rgba(5,16,30,0.05) 35%, rgba(5,16,30,0.8) 100%)',
            md: 'linear-gradient(90deg, rgba(5,16,30,0.78) 0%, rgba(5,16,30,0.42) 42%, rgba(5,16,30,0.04) 68%), linear-gradient(180deg, rgba(5,16,30,0.3) 0%, rgba(5,16,30,0) 30%, rgba(5,16,30,0.55) 100%)',
          },
        }}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: 1240,
          mx: 'auto',
          px: { xs: 2, md: 5 },
          pb: { xs: 5, md: 7 },
        }}
      >
        <Typography
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.6,
            borderRadius: '999px',
            backgroundColor: 'rgba(0,180,216,0.2)',
            border: '1px solid rgba(0,180,216,0.6)',
            color: '#9fe8ff',
            fontSize: '0.78rem',
            fontWeight: 800,
            letterSpacing: 1,
            textTransform: 'uppercase',
          }}
        >
          <BoltRoundedIcon sx={{ fontSize: 16 }} /> Coming soon · Blade Battery 2.0
        </Typography>
        <Typography
          component='h1'
          sx={{
            mt: 1.5,
            fontSize: { xs: '2.6rem', sm: '3.6rem', md: '5rem' },
            fontWeight: 900,
            lineHeight: 0.98,
          }}
        >
          BYD Atto 2
        </Typography>
        <Typography
          sx={{
            mt: 1,
            fontSize: { xs: '1.05rem', md: '1.3rem' },
            fontWeight: 700,
            color: '#9fe8ff',
          }}
        >
          The revolution is here. Siapkan diri Anda untuk EV SUV terkemuka.
        </Typography>
        {!isMobile && <HeroDetails />}
      </Box>
    </Box>

    {/* Di layar kecil, detail diletakkan di bawah foto supaya mobil tidak tertutup teks. */}
    {isMobile && (
      <Box sx={{ backgroundColor: '#0d1b2a', color: '#fff', px: 2, pt: 3, pb: 4 }}>
        <HeroDetails />
      </Box>
    )}
    </>
  );
}

function HeroDetails() {
  return (
    <>
      <Typography
        sx={{
          mt: { xs: 0, md: 1.5 },
          maxWidth: 620,
          fontSize: { xs: '1rem', md: '1.1rem' },
          lineHeight: 1.7,
          color: 'rgba(255,255,255,0.88)',
        }}
      >
        SUV kompak listrik BYD berikutnya sudah dipamerkan di Indonesia dan
        dipastikan hadir dengan Blade Battery 2.0 serta Flash Charging. Harga
        dan spesifikasi resmi menyusul. Daftar sekarang supaya jadi yang
        pertama tahu dari Junius di BYD BSD Serpong.
      </Typography>

      <Box sx={{ mt: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <Button
          href={WA_UPDATE}
          target='_blank'
          rel='noopener noreferrer'
          variant='contained'
          startIcon={<NotificationsActiveRoundedIcon />}
          sx={{
            px: 3,
            py: 1.3,
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 800,
            backgroundColor: '#00b4d8',
            color: '#06202b',
            '&:hover': { backgroundColor: '#48cae4' },
          }}
        >
          Daftar untuk update & eksklusif
        </Button>
        <Button
          href={WA_BOOKING}
          target='_blank'
          rel='noopener noreferrer'
          variant='outlined'
          startIcon={<WhatsAppIcon />}
          sx={{
            px: 3,
            py: 1.3,
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 800,
            color: '#fff',
            borderColor: 'rgba(255,255,255,0.55)',
            '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' },
          }}
        >
          Tanya pemesanan awal
        </Button>
      </Box>

      <Box
        sx={{
          mt: 3.5,
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
          gap: 1,
          maxWidth: 760,
        }}
      >
        {[
          ['Blade 2.0', 'Baterai generasi kedua'],
          ['9 menit', '10-97% Flash Charging*'],
          ['4,3 m', 'SUV kompak 5 penumpang'],
          ['2 tipe', 'Terdaftar di NJKB DKI'],
        ].map(([value, label]) => (
          <Box
            key={label}
            sx={{
              px: 1.5,
              py: 1.2,
              borderRadius: '10px',
              border: '1px solid rgba(255,255,255,0.22)',
              backgroundColor: 'rgba(5,16,30,0.45)',
              backdropFilter: 'blur(6px)',
            }}
          >
            <Typography sx={{ fontSize: { xs: '1.1rem', md: '1.35rem' }, fontWeight: 900 }}>
              {value}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.78)' }}>
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
      <Typography sx={{ mt: 1.2, fontSize: '0.74rem', color: 'rgba(255,255,255,0.6)' }}>
        *Klaim BYD untuk Blade Battery 2.0 dengan BYD Flash Charger. Angka
        resmi untuk Indonesia menyusul. Foto: BYD Atto 2 versi Eropa.
      </Typography>
    </>
  );
}

function TimelineSection() {
  return (
    <Box sx={{ backgroundColor: '#f4f6f8', px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 } }}>
      <Box sx={{ maxWidth: 1240, mx: 'auto' }}>
        <Eyebrow>Status terkini · diperbarui {UPDATED_AT}</Eyebrow>
        <SectionTitle>Perjalanan Atto 2 menuju Indonesia</SectionTitle>
        <Box
          sx={{
            mt: 4,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
            gap: 2,
          }}
        >
          {timeline.map((item, index) => {
            const isPending = index === timeline.length - 1;
            return (
              <Box
                key={item.title}
                sx={{
                  p: 2.5,
                  borderRadius: '14px',
                  backgroundColor: '#fff',
                  border: isPending
                    ? '1px dashed rgba(0,180,216,0.8)'
                    : '1px solid rgba(17,24,39,0.08)',
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    color: isPending ? '#0077a6' : '#fff',
                    backgroundColor: isPending ? 'rgba(0,180,216,0.14)' : '#0077a6',
                  }}
                >
                  {isPending ? (
                    <EventAvailableRoundedIcon sx={{ fontSize: 20 }} />
                  ) : (
                    <VerifiedRoundedIcon sx={{ fontSize: 20 }} />
                  )}
                </Box>
                <Typography
                  sx={{
                    mt: 1.5,
                    fontSize: '0.78rem',
                    fontWeight: 800,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                    color: '#0077a6',
                  }}
                >
                  {item.date}
                </Typography>
                <Typography sx={{ mt: 0.5, fontWeight: 900, color: '#111827', lineHeight: 1.25 }}>
                  {item.title}
                </Typography>
                <Typography sx={{ mt: 0.8, color: '#4b5563', lineHeight: 1.6, fontSize: '0.95rem' }}>
                  {item.desc}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

function Figure({ item, dark = false }) {
  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: '16px',
        overflow: 'hidden',
        backgroundColor: dark ? '#0f1115' : '#e5e7eb',
        '&::before': {
          content: '""',
          display: 'block',
          paddingTop: item.ratioMd
            ? { xs: `${item.ratio}%`, md: `${item.ratioMd}%` }
            : `${item.ratio}%`,
        },
        '&:hover img': { transform: 'scale(1.03)' },
      }}
    >
      <Box
        component='img'
        src={item.src}
        alt={`BYD Atto 2: ${item.title}`}
        loading='lazy'
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          transition: 'transform 0.5s ease',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          p: { xs: 2, md: 2.5 },
          pt: 6,
          background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.72) 100%)',
          color: '#fff',
        }}
      >
        <Typography sx={{ fontWeight: 900, fontSize: { xs: '1rem', md: '1.15rem' } }}>
          {item.title}
        </Typography>
        <Typography sx={{ mt: 0.4, fontSize: '0.9rem', lineHeight: 1.5, color: 'rgba(255,255,255,0.86)' }}>
          {item.desc}
        </Typography>
      </Box>
    </Box>
  );
}

function ExteriorSection() {
  const [wide, ...tall] = exteriorGallery;
  return (
    <Box sx={{ backgroundColor: '#fff', px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 } }}>
      <Box sx={{ maxWidth: 1240, mx: 'auto' }}>
        <Eyebrow>Eksterior</Eyebrow>
        <SectionTitle>SUV kompak dengan tampilan yang mencuri perhatian</SectionTitle>
        <Box
          sx={{
            mt: 4,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.6fr 1fr 1fr' },
            gap: 2,
            alignItems: 'stretch',
          }}
        >
          <Figure item={wide} />
          {tall.map((item) => (
            <Figure key={item.title} item={item} />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function InteriorSection() {
  const [wide, tall1, tall2, ...rest] = interiorGallery;
  return (
    <Box sx={{ backgroundColor: '#fff', px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 } }}>
      <Box sx={{ maxWidth: 1240, mx: 'auto' }}>
        <Eyebrow>Interior</Eyebrow>
        <SectionTitle>Kabin lapang dengan layar putar pintar</SectionTitle>
        <Box
          sx={{
            mt: 4,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.6fr 1fr 1fr' },
            gap: 2,
          }}
        >
          <Figure item={wide} />
          <Figure item={tall1} />
          <Figure item={tall2} />
        </Box>
        <Box
          sx={{
            mt: 2,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          {rest.map((item) => (
            <Figure key={item.title} item={item} />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function TechSection() {
  return (
    <Box sx={{ backgroundColor: '#0d1b2a', color: '#fff', px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 } }}>
      <Box sx={{ maxWidth: 1240, mx: 'auto' }}>
        <Eyebrow light>Teknologi, keselamatan, konektivitas</Eyebrow>
        <SectionTitle light>Yang bekerja di balik bodi kompaknya</SectionTitle>
        <Typography sx={{ mt: 1.5, maxWidth: 780, color: 'rgba(255,255,255,0.78)', lineHeight: 1.7 }}>
          Deskripsi mengikuti Atto 2 versi Eropa. Versi Indonesia dipastikan
          memakai Blade Battery 2.0 dengan Flash Charging, kelengkapan lain
          menunggu pengumuman resmi.
        </Typography>
        <Box
          sx={{
            mt: 4,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          {techFeatures.map((item) => (
            <Box
              key={item.title}
              sx={{
                borderRadius: '14px',
                overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.12)',
                backgroundColor: 'rgba(255,255,255,0.04)',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  backgroundColor: '#13263a',
                  '&::before': { content: '""', display: 'block', paddingTop: '56%' },
                }}
              >
                <Box
                  component='img'
                  src={item.src}
                  alt={`BYD Atto 2: ${item.title}`}
                  loading='lazy'
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              </Box>
              <Box sx={{ p: 2.2 }}>
                <Typography sx={{ fontWeight: 900, fontSize: '1.05rem' }}>{item.title}</Typography>
                <Typography sx={{ mt: 0.6, lineHeight: 1.6, fontSize: '0.95rem', color: 'rgba(255,255,255,0.78)' }}>
                  {item.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function StatusSection() {
  return (
    <Box sx={{ backgroundColor: '#fff', px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 } }}>
      <Box
        sx={{
          maxWidth: 1240,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { xs: 3, md: 4 },
        }}
      >
        <Box
          sx={{
            p: { xs: 2.5, md: 3.5 },
            borderRadius: '16px',
            backgroundColor: '#eaf8fb',
            border: '1px solid rgba(0,180,216,0.35)',
          }}
        >
          <Eyebrow>Sudah dipastikan</Eyebrow>
          <SectionTitle small>Yang sudah kami ketahui</SectionTitle>
          <Box component='ul' sx={{ mt: 2, pl: 2.5, m: 0, color: '#1f2937', lineHeight: 1.7 }}>
            {confirmed.map((item) => (
              <Box component='li' key={item} sx={{ mb: 1 }}>
                {item}
              </Box>
            ))}
          </Box>
        </Box>
        <Box
          sx={{
            p: { xs: 2.5, md: 3.5 },
            borderRadius: '16px',
            backgroundColor: '#f8f9fb',
            border: '1px solid rgba(17,24,39,0.1)',
          }}
        >
          <Eyebrow>Menunggu pengumuman resmi</Eyebrow>
          <SectionTitle small>Yang masih kami tunggu</SectionTitle>
          <Box component='ul' sx={{ mt: 2, pl: 2.5, m: 0, color: '#1f2937', lineHeight: 1.7 }}>
            {pending.map((item) => (
              <Box component='li' key={item} sx={{ mb: 1 }}>
                {item}
              </Box>
            ))}
          </Box>
          <Typography sx={{ mt: 2, fontSize: '0.82rem', color: '#6b7280' }}>
            Sumber: pernyataan BYD Motor Indonesia (14 Oktober 2026) yang
            dikutip Kompas.com dan OTO.com, data NJKB Samsat DKI Jakarta, serta
            halaman resmi BYD Eropa untuk foto dan fitur.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function ReferenceSpecSection() {
  return (
    <Box sx={{ backgroundColor: '#f4f6f8', px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 } }}>
      <Box sx={{ maxWidth: 1240, mx: 'auto' }}>
        <Eyebrow>Referensi spesifikasi</Eyebrow>
        <SectionTitle>Seperti apa Atto 2 di pasar lain</SectionTitle>
        <Typography sx={{ mt: 1.5, maxWidth: 800, color: '#4b5563', lineHeight: 1.7 }}>
          Atto 2 sudah dijual di Eropa, Tiongkok, dan Malaysia. Angka berlabel
          versi Eropa diambil dari halaman resmi BYD Eropa. Tenaga motor dan
          kapasitas baterai versi Indonesia menunggu pengumuman resmi.
        </Typography>
        <Box
          component='table'
          sx={{
            mt: 3,
            width: '100%',
            maxWidth: 960,
            borderCollapse: 'collapse',
            backgroundColor: '#fff',
            borderRadius: '12px',
            overflow: 'hidden',
            border: '1px solid rgba(17,24,39,0.08)',
            '& td': {
              px: 2,
              py: 1.2,
              borderTop: '1px solid rgba(17,24,39,0.08)',
              fontSize: '0.95rem',
              verticalAlign: 'top',
            },
            '& tr:first-of-type td': { borderTop: 0 },
            '& td:first-of-type': { color: '#4b5563', width: '38%' },
            '& td:last-of-type': { fontWeight: 700, color: '#111827' },
          }}
        >
          <tbody>
            {referenceSpecs.map(([label, value]) => (
              <tr key={label}>
                <td>{label}</td>
                <td>{value}</td>
              </tr>
            ))}
          </tbody>
        </Box>
      </Box>
    </Box>
  );
}

function LineupSection() {
  const siblings = [
    {
      name: 'BYD Atto 1',
      role: 'City car listrik untuk harian',
      detail: 'Mulai Rp199 juta, NIK 2026',
      path: '/jelajahi-atto1',
    },
    {
      name: 'BYD Atto 2',
      role: 'SUV kompak listrik, segera hadir',
      detail: 'Blade Battery 2.0 + Flash Charging',
      path: '/jelajahi-atto2',
      highlight: true,
    },
    {
      name: 'BYD Atto 3',
      role: 'SUV listrik keluarga',
      detail: 'Advanced Plus Rp415 juta',
      path: '/jelajahi-atto',
    },
  ];

  return (
    <Box sx={{ backgroundColor: '#fff', px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 } }}>
      <Box sx={{ maxWidth: 1240, mx: 'auto' }}>
        <Eyebrow>Posisi di lini Atto</Eyebrow>
        <SectionTitle>Di antara Atto 1 dan Atto 3</SectionTitle>
        <Box
          sx={{
            mt: 3,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          {siblings.map((item) => (
            <Box
              key={item.name}
              component={RouterLink}
              to={item.path}
              sx={{
                p: 2.5,
                borderRadius: '14px',
                textDecoration: 'none',
                color: 'inherit',
                border: item.highlight
                  ? '2px solid #00b4d8'
                  : '1px solid rgba(17,24,39,0.1)',
                backgroundColor: item.highlight ? '#eaf8fb' : '#f8f9fb',
                transition: 'transform 0.2s ease',
                '&:hover': { transform: 'translateY(-3px)' },
              }}
            >
              <Typography sx={{ fontWeight: 900, fontSize: '1.15rem', color: '#111827' }}>
                {item.name}
              </Typography>
              <Typography sx={{ mt: 0.4, color: '#4b5563' }}>{item.role}</Typography>
              <Typography sx={{ mt: 1, fontSize: '0.9rem', fontWeight: 700, color: '#0077a6' }}>
                {item.detail}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function FaqSection() {
  return (
    <Box sx={{ backgroundColor: '#f4f6f8', px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 } }}>
      <Box sx={{ maxWidth: 980, mx: 'auto' }}>
        <Eyebrow>FAQ</Eyebrow>
        <SectionTitle>Pertanyaan yang sering masuk</SectionTitle>
        <Box sx={{ mt: 3, display: 'grid', gap: 1.5 }}>
          {faqs.map((item) => (
            <Box
              key={item.q}
              sx={{
                p: 2.5,
                borderRadius: '12px',
                backgroundColor: '#fff',
                border: '1px solid rgba(17,24,39,0.08)',
              }}
            >
              <Typography component='h3' sx={{ fontWeight: 900, color: '#111827', fontSize: '1.05rem' }}>
                {item.q}
              </Typography>
              <Typography sx={{ mt: 0.8, color: '#4b5563', lineHeight: 1.7 }}>
                {item.a}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function CtaSection() {
  return (
    <Box sx={{ px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 }, backgroundColor: '#07111f', color: '#fff' }}>
      <Box
        sx={{
          maxWidth: 1240,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { xs: 3, md: 5 },
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            borderRadius: '20px',
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.14)',
            boxShadow: '0 30px 60px rgba(0,0,0,0.45)',
          }}
        >
          <Box
            component='img'
            src={poster}
            alt='Poster BYD Atto 2 coming soon di BYD BSD Serpong'
            loading='lazy'
            width={1195}
            height={896}
            sx={{ width: '100%', height: 'auto', display: 'block' }}
          />
        </Box>
        <Box>
          <Eyebrow light>BYD BSD Serpong</Eyebrow>
          <Typography component='h2' sx={{ mt: 1, fontSize: { xs: '2rem', md: '3rem' }, fontWeight: 900, lineHeight: 1.05 }}>
            Jadi yang pertama tahu
          </Typography>
          <Typography sx={{ mt: 2, color: 'rgba(255,255,255,0.8)', lineHeight: 1.7 }}>
            Daftarkan nomor WhatsApp Anda ke Junius. Begitu BYD mengumumkan
            harga, spesifikasi, dan jadwal peluncuran Atto 2, Anda langsung
            dikabari, termasuk penawaran eksklusif untuk pemesan awal di BYD BSD
            Serpong.
          </Typography>
          <Box sx={{ mt: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              href={WA_UPDATE}
              target='_blank'
              rel='noopener noreferrer'
              variant='contained'
              startIcon={<WhatsAppIcon />}
              sx={{
                px: 4,
                py: 1.3,
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 800,
                backgroundColor: '#25D366',
                '&:hover': { backgroundColor: '#1EBE54' },
              }}
            >
              Daftar update BYD Atto 2
            </Button>
            <Button
              href={WA_BOOKING}
              target='_blank'
              rel='noopener noreferrer'
              variant='outlined'
              sx={{
                px: 3,
                py: 1.3,
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 800,
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.45)',
                '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' },
              }}
            >
              Tanya pemesanan awal
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function Eyebrow({ children, light = false }) {
  return (
    <Typography
      sx={{
        color: light ? '#9fe8ff' : '#0077a6',
        fontWeight: 800,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        fontSize: '0.82rem',
      }}
    >
      {children}
    </Typography>
  );
}

function SectionTitle({ children, small = false, light = false }) {
  return (
    <Typography
      component='h2'
      sx={{
        mt: 1,
        fontSize: small ? { xs: '1.5rem', md: '1.9rem' } : { xs: '2rem', md: '3rem' },
        fontWeight: 900,
        color: light ? '#fff' : '#111827',
        lineHeight: 1.05,
      }}
    >
      {children}
    </Typography>
  );
}

export default JelajahiAtto2;
